import { type IDataObject } from '@inpart-io/devtools-iap';
import { GET_ALL, SESSION_MODULE } from '@inpart-io/devtools-vue';
import { DataType, type IDealType, type ILeadStatusConfiguration, type ITemplateDealStage, LeadStatus } from '@inpart-io/padme-iap';
import { dealTypeProperties } from '@inpart-io/padme-iap';
import { computed, type ComputedRef, onBeforeMount, type Ref, ref, watch } from 'vue';
import { useStore } from 'vuex';

import { type LeadStatusOption, LeadStatusSet } from '@/components/lead-profile/components/lead-status/constants/lead.status.constants';
import { type ITreeOption } from '@/components/select-tree/services/tree-helper';
import { LeadDealType } from '@/constants/deal-types.constants';
import { leadStatusService } from '@/services/lead-status-service';
import { translationService } from '@/services/translation-service';
import { DEAL_TYPE_MODULE } from '@/store/deal-type-module';
import { RETRIEVE_ALL_DEAL_TYPES } from '@/store/deal-type-module/actions';
import { LEAD_STATUS_CONFIGURATION_MODULE } from '@/store/lead-status-configuration-module';
import { GET_FROM_STATUS } from '@/store/lead-status-configuration-module/getters';
import { USE_DEAL_WORKFLOW } from '@/store/session-module/getters';
import { TEMPLATE_DEAL_STAGE_MODULE } from '@/store/template-deal-stage-module';

export type StageOption = ILeadStatusConfiguration | ITemplateDealStage;

export interface IDealTypeDictionaryEntry extends IDataObject {
    label: string;
}

export const LEAD_TOKEN = '_L_';
export const DEAL_TOKEN = '_D_';

const dealTypeDictionary = ref<Map<string, IDealTypeDictionaryEntry>>(new Map());

const OLD_LEAD_DEAL_TYPE_ID = '-1000';

export function useWorkflowFiltersHelper(briefId?: ComputedRef<number>): {
    dealTypeDictionary: Ref<Map<string, IDealTypeDictionaryEntry>>;
    dealTypeDictionaryKeys: ComputedRef<string[]>;
    dealTypeTreeOptions: ComputedRef<ITreeOption[]>;
    dealTypeOptions: ComputedRef<IDealType[]>;
    useDealWorkflow: ComputedRef<boolean>;
    statusOptionsMap: Ref<Map<string, LeadStatusOption>>;
    buildTypeStageKey: (dealTypeId: number, stage: ILeadStatusConfiguration | ITemplateDealStage) => string;
    getDealTypeLabelFromKey: (key: string) => IDealTypeDictionaryEntry;
    updateDealTypeDictionary: () => void;
    retrieveDealTypes: () => Promise<number[]>;
    convertWorkflowTypesToStages: (workflowTypesFilter: string, dealTypeToExcludes: number[]) => string[];
    parseOldDealTypeFilter: (oldDealTypes: string, oldDealStages?: string, oldLeadStages?: string) => string[];
} {
    const store = useStore();

    const useDealWorkflow = computed<boolean>(() => {
        return store.getters[`${SESSION_MODULE}/${USE_DEAL_WORKFLOW}`];
    });

    const dealTypeOptions = computed<IDealType[]>(() => {
        const all = useDealWorkflow.value ? store.getters[`${DEAL_TYPE_MODULE}/${GET_ALL}`] : [];
        return [LeadDealType, ...all];
    });

    const leadStages = computed<ILeadStatusConfiguration[]>(() => {
        const validStages = leadStatusService.getLeadStatusToFilterByStage();
        return validStages.map((status) => {
            return store.getters[`${LEAD_STATUS_CONFIGURATION_MODULE}/${GET_FROM_STATUS}`](status);
        });
    });

    const closedLeadStage = computed<ILeadStatusConfiguration>(() => {
        return store.getters[`${LEAD_STATUS_CONFIGURATION_MODULE}/${GET_FROM_STATUS}`](LeadStatus.CLOSED);
    });

    const dealStages = computed<ITemplateDealStage[]>(() => {
        return store.getters[`${TEMPLATE_DEAL_STAGE_MODULE}/${GET_ALL}`] ?? [];
    });

    const statusOptionsMap = ref(
        new Map<string, LeadStatusOption>([
            [
                LeadStatusSet.ACTIVE,
                {
                    key: LeadStatusSet.ACTIVE,
                    label: LeadStatusSet.ACTIVE,
                    values: leadStatusService.getStatusSetValues(LeadStatusSet.ACTIVE),
                    count: 0
                }
            ],
            [
                LeadStatus.ON_HOLD,
                {
                    key: LeadStatus.ON_HOLD,
                    label: translationService.getLeadStatusLabel(LeadStatus.ON_HOLD),
                    values: [LeadStatus.ON_HOLD],
                    count: 0
                }
            ],
            [
                LeadStatusSet.CLOSED,
                {
                    key: LeadStatusSet.CLOSED,
                    label: translationService.getLeadStatusLabel(LeadStatusSet.CLOSED),
                    values: leadStatusService.getStatusSetValues(LeadStatusSet.CLOSED),
                    count: 0
                }
            ],
            [
                LeadStatus.DISQUALIFIED,
                {
                    key: LeadStatus.DISQUALIFIED,
                    label: translationService.getLeadStatusLabel(LeadStatus.DISQUALIFIED),
                    values: [LeadStatus.DISQUALIFIED],
                    count: 0
                }
            ]
        ])
    );

    const retrieveDealTypes = async (): Promise<number[]> => {
        return store.dispatch(`${DEAL_TYPE_MODULE}/${RETRIEVE_ALL_DEAL_TYPES}`, {
            properties: dealTypeProperties.DEFAULT
        });
    };

    const buildTypeStageKey = (dealTypeId: number, stage: ILeadStatusConfiguration | ITemplateDealStage): string => {
        return `${dealTypeId}${stage?.['@type'] === DataType.TemplateDealStage ? DEAL_TOKEN : LEAD_TOKEN}${stage?.dobjId}`;
    };

    const buildStageTreeOption = (dealType: IDealType, stage: ITemplateDealStage | ILeadStatusConfiguration, isDeal = true): ITreeOption => {
        return { key: buildTypeStageKey(dealType.dobjId, stage), dobjId: stage.dobjId, label: stage.name };
    };

    const leadStageTreeOptions = computed<ITreeOption[]>(() => {
        return (
            leadStages.value
                ?.filter((leadStage) => {
                    return leadStage;
                })
                .map((leadStage) => {
                    return buildStageTreeOption(LeadDealType, leadStage, false);
                }) ?? []
        );
    });

    const dealTypeTreeOptions = computed<ITreeOption[]>(() => {
        if (!useDealWorkflow.value) {
            return leadStageTreeOptions.value.slice();
        } else {
            return (
                dealTypeOptions.value
                    ?.filter((dealType) => {
                        return (
                            dealStages.value?.filter((ds) => {
                                return ds.onDealType === dealType.dobjId;
                            }).length !== 0 || dealType.dobjId === LeadDealType.dobjId
                        );
                    })
                    .map((dealType) => {
                        // Deal type: First level element
                        const dealTypeOption: ITreeOption = { key: dealType.dobjId.toString(), dobjId: dealType.dobjId, label: dealType.name };

                        // Stages: Second level elements
                        // First, the simple stages for Leads (ILeadStatusConfiguration)
                        dealTypeOption.children = leadStages.value
                            ?.filter((leadStage) => {
                                return leadStage;
                            })
                            .map((leadStage) => {
                                return buildStageTreeOption(dealType, leadStage, false);
                            });

                        // Second, the deal stages for Deals (ITemplateDealStage) & closed lead Stage
                        if (LeadDealType.dobjId !== dealType.dobjId) {
                            dealTypeOption.children.push(
                                ...dealStages.value
                                    ?.filter((ds) => {
                                        return ds.onDealType === dealType.dobjId;
                                    })
                                    ?.sort((a, b) => {
                                        return a.position - b.position;
                                    })
                                    ?.map((stage) => {
                                        return buildStageTreeOption(dealType, stage);
                                    })
                            );
                            // Closed stage for deals
                            if (closedLeadStage?.value) {
                                const closedDealStageOption = buildStageTreeOption(dealType, closedLeadStage.value, false);
                                dealTypeOption.children.push(closedDealStageOption);
                            }
                        }
                        return dealTypeOption;
                    }) ?? []
            );
        }
    });

    const getDealTypeLabelFromKey = (key: string): IDealTypeDictionaryEntry => {
        return dealTypeDictionary.value.get(key);
    };

    const updateDealTypeDictionary = (): void => {
        dealTypeTreeOptions.value?.forEach((dealTypeOption) => {
            // Add the first level elements to the dictionary
            dealTypeDictionary.value.set(dealTypeOption.key, {
                dobjId: dealTypeOption.dobjId,
                label: dealTypeOption.label
            });

            // Add the second level elements to the dictionary
            dealTypeOption.children?.forEach((stageOption) => {
                dealTypeDictionary.value.set(stageOption.key, {
                    dobjId: dealTypeOption.dobjId,
                    label: dealTypeOption.label
                });
            });
        });
    };

    const dealTypeDictionaryKeys = computed<string[]>(() => {
        return Array.from(dealTypeDictionary.value.keys());
    });

    const convertWorkflowTypesToStages = (workflowTypesFilter: string, dealTypeToExcludes: number[] = []): string[] => {
        const workflowTypes = (workflowTypesFilter?.trim()?.length ?? 0) > 0 ? workflowTypesFilter.split(',') : [];
        const dealTypeIds = new Set(workflowTypes);
        const typeStageKeys: string[] = [];
        dealTypeIds.forEach((dealTypeId) => {
            const id = dealTypeId === OLD_LEAD_DEAL_TYPE_ID ? LeadDealType.dobjId : dealTypeId;
            const toAdd = dealTypeDictionaryKeys.value.filter((key) => {
                return (key.startsWith(`${id}${DEAL_TOKEN}`) || key.startsWith(`${id}${LEAD_TOKEN}`)) && !dealTypeToExcludes.includes(Number(id));
            });
            typeStageKeys.push(...toAdd);
        });

        return typeStageKeys;
    };

    // Added Support to the old dealType/dealStage/LeadStage filter
    // It will extract the values from the old filter for searching in the dictionary for all the needed template stage keys
    const parseOldDealTypeFilter = (oldDealTypeFilter: string, oldDealStageFilter?: string, oldLeadStageFilter?: string): string[] => {
        const oldSelectedDealTypes = (oldDealTypeFilter?.trim()?.length ?? 0) > 0 ? oldDealTypeFilter.split(',') : [];
        const typeStageKeys: string[] = [];
        const oldSelectedDealStages = (oldDealStageFilter?.trim()?.length ?? 0) > 0 ? oldDealStageFilter.split(',') : [];
        const oldSelectedLeadStages = (oldLeadStageFilter?.trim()?.length ?? 0) > 0 ? oldLeadStageFilter.split(',') : [];

        if (useDealWorkflow.value && oldSelectedDealTypes.length > 0) {
            let dealTypeIds = new Set(oldSelectedDealTypes);
            const dealStageIds = new Set(oldSelectedDealStages);
            const selectedDealTypeIds = new Set<string>();
            if (oldSelectedDealStages.length > 0) {
                dealStageIds.forEach((dealStageId) => {
                    const toAdd = dealTypeDictionaryKeys.value.filter((key) => {
                        const dealTypeId = key.split('_')[0];
                        if (key.endsWith(`${DEAL_TOKEN}${dealStageId}`) && !selectedDealTypeIds.has(dealTypeId)) {
                            selectedDealTypeIds.add(dealTypeId);
                        }
                        return key.endsWith(`${DEAL_TOKEN}${dealStageId}`);
                    });
                    typeStageKeys.push(...toAdd);
                });
            }
            if (selectedDealTypeIds.size !== dealTypeIds.size || oldSelectedDealStages.length === 0) {
                const dealTypesToApply = Array.from(dealTypeIds).filter((dealTypeId: string) => {
                    return !selectedDealTypeIds.has(dealTypeId);
                });
                dealTypeIds = new Set(dealTypesToApply);
                dealTypeIds.forEach((dealTypeId) => {
                    const id = dealTypeId === OLD_LEAD_DEAL_TYPE_ID ? LeadDealType.dobjId : dealTypeId;
                    if (dealTypeId !== OLD_LEAD_DEAL_TYPE_ID || oldSelectedLeadStages.length === 0) {
                        const toAdd = dealTypeDictionaryKeys.value.filter((key) => {
                            return key.startsWith(`${id}${DEAL_TOKEN}`) || key.startsWith(`${id}${LEAD_TOKEN}`);
                        });
                        typeStageKeys.push(...toAdd);
                    }
                });
            }
        }

        if (oldSelectedLeadStages.length > 0) {
            const leadStageIds = new Set(oldSelectedLeadStages);
            leadStageIds.forEach((leadStageId) => {
                typeStageKeys.push(`${LeadDealType.dobjId}${LEAD_TOKEN}${leadStageId}`);
            });
        }
        return typeStageKeys;
    };

    onBeforeMount(() => {
        updateDealTypeDictionary();
    });

    watch(dealTypeTreeOptions, () => {
        updateDealTypeDictionary();
    });

    return {
        buildTypeStageKey,
        dealTypeDictionary,
        dealTypeDictionaryKeys,
        dealTypeTreeOptions,
        getDealTypeLabelFromKey,
        dealTypeOptions,
        useDealWorkflow,
        updateDealTypeDictionary,
        retrieveDealTypes,
        convertWorkflowTypesToStages,
        parseOldDealTypeFilter,
        statusOptionsMap
    };
}
